<script setup lang="ts">
/**
 * Page « Donoor dans un pays » — /belgium, /luxembourg, /netherlands et /germany (analyse GEO du
 * 01/11/2026). Le site ne prononçait jamais « ASBL », « vzw », « 281.71 »,
 * « Bancontact » ni « organisme agréé » : une association belge ou
 * luxembourgeoise (ou une IA qui répond à sa question) ne trouvait aucune page
 * qui la concerne. Une page par pays, en 6 langues, charte « apple » des pages
 * secteur (modèle /collecte-dons-ecole), faits vérifiés dans le code
 * (packages/utils/src/recusFiscauxPays.ts, moyensPaiementLocaux.ts) et aux
 * sources officielles (SPF Finances, impotsdirects.public.lu).
 *
 * Adresses en anglais dès la création (règle du 25/10) ; aucune incidence
 * pour les associations françaises : rien ne change dans leurs parcours.
 *
 * Pays-Bas (01/11/2026) : ANBI, justificatif de don, iDEAL | Wero, numéro
 * KvK — faits du code (recusFiscauxPays.ts, moyensPaiementLocaux.ts) et de la
 * Belastingdienst (giftenaftrek 2026).
 *
 * Allemagne (03/11/2026) : e.V. (numéro de registre + tribunal),
 * Zuwendungsbestätigung au modèle officiel du BMF, déduction du revenu
 * (Sonderausgaben, § 10b EStG) — faits du code (recusFiscauxPays.ts,
 * moyensPaiementLocaux.ts, numerosOfficiels.json DE_VR). À mettre en ligne
 * AVEC l'ouverture des paiements allemands (backup-manager › Pays).
 */
import AppelFinalApple from "~/components/shared/AppelFinalApple.vue";
import PagesLieesApple, { type PageLiee } from "~/components/shared/PagesLieesApple.vue";
import type { CarteFactuelle } from "~/components/shared/BlocFactuelApple.vue";
import type { Atout } from "~/components/shared/AtoutsApple.vue";
import { useLienLocal } from "~/composables/useLienLocal";
import { langueDuChemin, LOCALE_INTL } from "~/utils/langue";

type CodePays = "be" | "lu" | "nl" | "de";

/** Ce qui change d'un pays à l'autre, hors textes. */
const CONFIG: Record<CodePays, {
  chemin: string;
  codeIso: string;
  nomPays: string;
  ogImage: string;
  cartes: { href: string; image: string }[];
  liees: { href: string; icone: string }[];
  /** Guides du blog (en français seulement ; masqués s'ils ne sont pas publiés). */
  guides: string[];
}> = {
  be: {
    chemin: "/belgium",
    codeIso: "BE",
    nomPays: "Belgique",
    ogImage: "https://donoor.org/og-image.png",
    cartes: [
      { href: "/recus-fiscaux", image: "/assets/themes/recu/1.webp" },
      { href: "/page-don", image: "/assets/themes/donligne/1.webp" },
      { href: "/conformite", image: "/assets/themes/association/reunion.webp" },
      { href: "/tap-to-pay", image: "/assets/themes/taptopay/1.webp" },
    ],
    liees: [
      { href: "/recus-fiscaux", icone: "i-lucide-file-check-2" },
      { href: "/cagnotte", icone: "i-lucide-target" },
      { href: "/billetterie", icone: "i-lucide-ticket" },
      { href: "/adhesion", icone: "i-lucide-id-card" },
    ],
    guides: [
      "belgium-donation-tax-reduction",
      "belgium-281-71-tax-certificate",
      "belgium-donor-national-number",
      "belgium-online-donation-deductible",
      "belgium-mosque-tax-certificate",
    ],
  },
  lu: {
    chemin: "/luxembourg",
    codeIso: "LU",
    nomPays: "Luxembourg",
    ogImage: "https://donoor.org/og-image.png",
    cartes: [
      { href: "/recus-fiscaux", image: "/assets/themes/recu/1.webp" },
      { href: "/recus-fiscaux", image: "/assets/themes/recu/3.webp" },
      { href: "/tap-to-pay", image: "/assets/themes/taptopay/2.webp" },
      { href: "/conformite", image: "/assets/themes/association/reunion.webp" },
    ],
    liees: [
      { href: "/recus-fiscaux", icone: "i-lucide-file-check-2" },
      { href: "/cagnotte", icone: "i-lucide-target" },
      { href: "/tap-to-pay", icone: "i-lucide-smartphone-nfc" },
      { href: "/adhesion", icone: "i-lucide-id-card" },
    ],
    guides: ["luxembourg-tax-deductible-donations", "luxembourg-payconiq-association-payments"],
  },
  nl: {
    chemin: "/netherlands",
    codeIso: "NL",
    nomPays: "Pays-Bas",
    ogImage: "https://donoor.org/og-image.png",
    cartes: [
      { href: "/recus-fiscaux", image: "/assets/themes/recu/1.webp" },
      { href: "/page-don", image: "/assets/themes/donligne/1.webp" },
      { href: "/recus-fiscaux", image: "/assets/themes/recu/3.webp" },
      { href: "/tap-to-pay", image: "/assets/themes/taptopay/1.webp" },
    ],
    liees: [
      { href: "/recus-fiscaux", icone: "i-lucide-file-check-2" },
      { href: "/cagnotte", icone: "i-lucide-target" },
      { href: "/tap-to-pay", icone: "i-lucide-smartphone-nfc" },
      { href: "/adhesion", icone: "i-lucide-id-card" },
    ],
    // Aucun guide néerlandais publié pour l'instant : le bloc se masque.
    guides: [],
  },
  de: {
    chemin: "/germany",
    codeIso: "DE",
    nomPays: "Allemagne",
    ogImage: "https://donoor.org/og-image.png",
    cartes: [
      { href: "/recus-fiscaux", image: "/assets/themes/recu/1.webp" },
      { href: "/page-don", image: "/assets/themes/donligne/1.webp" },
      { href: "/conformite", image: "/assets/themes/association/reunion.webp" },
      { href: "/tap-to-pay", image: "/assets/themes/taptopay/1.webp" },
    ],
    liees: [
      { href: "/recus-fiscaux", icone: "i-lucide-file-check-2" },
      { href: "/cagnotte", icone: "i-lucide-target" },
      { href: "/tap-to-pay", icone: "i-lucide-smartphone-nfc" },
      { href: "/adhesion", icone: "i-lucide-id-card" },
    ],
    // Aucun guide allemand publié pour l'instant : le bloc se masque.
    guides: [],
  },
};

const NB_FAQ: Record<CodePays, number> = { be: 9, lu: 7, nl: 10, de: 10 };
const ICONES_ATOUTS = ["i-lucide-badge-euro", "i-lucide-landmark", "i-lucide-layout-grid"];

const props = defineProps<{ pays: CodePays }>();

const { t } = useI18n({ useScope: "local" });
const route = useRoute();
const lienLocal = useLienLocal();
const config = CONFIG[props.pays];
const url = `https://donoor.org${config.chemin}`;
const p = (cle: string) => t(`${props.pays}.${cle}`);

const faqItems = computed(() =>
  Array.from({ length: NB_FAQ[props.pays] }, (_, i) => ({
    question: p(`faq.q${i + 1}.q`),
    answer: p(`faq.q${i + 1}.r`),
  })),
);

const cartesFactuelles = computed<CarteFactuelle[]>(() =>
  config.cartes.map((carte, i) => ({
    titre: p(`geo.c${i + 1}.titre`),
    texte: p(`geo.c${i + 1}.texte`),
    image: carte.image,
    alt: p(`geo.c${i + 1}.titre`),
    href: lienLocal(carte.href),
  })),
);

const atouts = computed<Atout[]>(() =>
  ICONES_ATOUTS.map((icone, i) => ({ titre: p(`atouts.a${i + 1}.titre`), texte: p(`atouts.a${i + 1}.texte`), icone })),
);

const puces = computed(() => [1, 2, 3, 4].map((i) => p(`fiscal.p${i}`)));

const pagesLiees = computed<PageLiee[]>(() =>
  config.liees.map((page, i) => ({
    title: p(`liees.p${i + 1}.titre`),
    description: p(`liees.p${i + 1}.description`),
    href: lienLocal(page.href),
    icon: page.icone,
  })),
);

useSeoMeta({
  title: p("seo.title"),
  description: p("seo.description"),
  ogTitle: p("seo.ogTitle"),
  ogDescription: p("seo.description"),
  ogImage: config.ogImage,
  ogUrl: url,
  twitterCard: "summary_large_image",
});

useHead({
  link: [{ rel: "canonical", href: url }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: () =>
        JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          url,
          name: p("seo.title"),
          inLanguage: LOCALE_INTL[langueDuChemin(route.path)],
          isPartOf: { "@id": "https://donoor.org/#website" },
          about: { "@type": "Country", name: config.nomPays, identifier: config.codeIso },
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2"] },
        }),
    },
    {
      type: "application/ld+json",
      innerHTML: () =>
        JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: p("seo.ogTitle"),
          description: p("seo.description"),
          url,
          provider: { "@id": "https://donoor.org/#organization" },
          areaServed: { "@type": "Country", name: config.nomPays, identifier: config.codeIso },
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        }),
    },
    {
      type: "application/ld+json",
      innerHTML: () =>
        JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.value.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
          })),
        }),
    },
  ],
});

useBreadcrumbSchema([
  { name: t("commun.accueil"), url: "https://donoor.org" },
  { name: p("hero.fil"), url },
]);

const racine = ref<HTMLElement | null>(null);
useApparitionAuDefilement(racine);
</script>

<template>
  <div ref="racine" class="min-h-screen bg-white">
    <HeroApple
      :accueil="t('commun.accueil')"
      :fil="p('hero.fil')"
      :titre="p('hero.titre')"
      :titre-accent="p('hero.titreAccent')"
      :description="p('hero.description')"
      :cta="p('hero.cta')"
      :cta-source="`pays-${props.pays}-hero`"
    >
      <template #visuel>
        <PhotoApple
          image="/assets/qr/donate.webp"
          :largeur="1600"
          :hauteur="893"
          :alt="p('hero.alt')"
          :pastille="p('hero.badge')"
          pastille-icone="i-lucide-heart"
          prioritaire
          max="max-w-[880px]"
          sizes="xs:92vw sm:92vw lg:880px"
        />
      </template>
    </HeroApple>

    <!-- Bloc factuel citable (GEO) : les faits du pays en un coup d'œil. -->
    <BlocFactuelApple
      :titre="p('geo.titre')"
      :cartes="cartesFactuelles"
      :lien-carte="t('commun.decouvrir')"
      :cta-texte="t('commun.cta')"
      :cta-source="`pays-${props.pays}`"
    >
      <template #intro>
        <i18n-t :keypath="`${props.pays}.geo.intro`" tag="p" scope="parent">
          <template #reduction>
            <span class="font-bold text-[#1d1d1f]">{{ p("geo.reduction") }}</span>
          </template>
        </i18n-t>
      </template>
    </BlocFactuelApple>

    <AtoutsApple :titre="p('atouts.titre')" :atouts="atouts" />

    <TexteMediaApple
      :titre="p('fiscal.titre')"
      :description="p('fiscal.description')"
      :puces="puces"
      media="gauche"
      fond="gris"
    >
      <template #media>
        <PhotoApple
          image="/billetterie/page-don.webp"
          :largeur="2000"
          :hauteur="1188"
          :alt="p('fiscal.alt')"
          max="max-w-none"
          sizes="xs:92vw sm:92vw lg:560px"
        />
      </template>
    </TexteMediaApple>

    <FaqSectionApple :items="faqItems" />

    <PagesLieesApple :titre="p('liees.titre')" :pages="pagesLiees" />

    <LazyVisibleBlogRelatedLinks v-if="config.guides.length" hydrate-on-visible :title="t('commun.guides')" :slugs="config.guides" />

    <AppelFinalApple />
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/components/pays/PagePays.json"></i18n>
